import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import AccountsSubNav from '../components/AccountsSubNav'
import SharedFilters from '../../../../components/SharedFilters'
import AppActionMenu from '../../../../components/AppActionMenu'
import AppPagination from '../../../../components/AppPagination'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { listAccountHeads, archiveAccountHead, deleteAccountHead, getAcGroups } from '../heads/api/acHeadApi'

export default function CustCashListScreen() {
  const navigate = useNavigate()
  const [searchField, setSearchField] = useState('Name')
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState('Active')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [cashGroupId, setCashGroupId] = useState(null)

  useEffect(() => {
    getAcGroups().then(groups => {
      const cGroup = groups.find(g => (g.Name || '').toLowerCase().includes('cash'))
      if (cGroup) setCashGroupId(cGroup.ID)
    }).catch(() => {})
  }, [])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = {
        searchText: searchText || undefined,
        archive: statusFilter === 'All' ? undefined : (statusFilter === 'Archive' ? 1 : 0),
        groupId: cashGroupId || undefined,
        page,
        pageSize: 25,
      }
      const data = await listAccountHeads(params)
      const rows = (Array.isArray(data) ? data : data?.items || []).map(row => ({
        id: row.ID,
        name: row.Name,
        address: row.Remarks || '—',
        gst: row.HSNSAC || '—',
        mobile: row.Alias || '—',
        createdOn: '—',
        lastUpdate: '—',
        status: row.Archive ? 'Archive' : 'Active',
      }))
      setItems(rows)
    } catch (e) {
      console.error('Failed to load cash customers', e)
    } finally {
      setIsLoading(false)
    }
  }, [searchText, statusFilter, cashGroupId, page])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleReset = () => {
    setSearchField('Name')
    setSearchText('')
    setStatusFilter('Active')
    setPage(1)
  }

  const handleArchive = async (id) => {
    try {
      await archiveAccountHead(id)
      appToast.success('Cash Customer status updated.')
      loadData()
    } catch (e) {
      appToast.error('Failed to update Cash Customer status.')
    }
  }

  const handleDelete = async (id, name) => {
    const confirmed = await appConfirm({
      title: 'Delete Cash Customer',
      message: `Delete Cash Customer "${name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await deleteAccountHead(id)
      appToast.success(`Cash customer "${name}" deleted successfully.`)
      loadData()
    } catch (e) {
      appToast.error('Failed to delete cash customer.')
    }
  }

  const filteredItems = items

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Financial Master</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Customers (Cash)
          </h1>
        </div>
        <AccountsSubNav />
      </section>

      {/* Shared Filters Accordion */}
      <SharedFilters
        searchField={searchField}
        searchText={searchText}
        statusFilter={statusFilter}

        setSearchField={setSearchField}
        setSearchText={setSearchText}
        setStatusFilter={setStatusFilter}
        onReset={handleReset}

        searchFieldOptions={[
          { value: 'Mobile No.', label: 'Mobile No.' },
          { value: 'Name', label: 'Name' }
        ]}
        statusOptions={[{ value: 'Active', label: 'Active' }, { value: 'Archive', label: 'Archived' }, { value: 'All', label: 'All' }]}
        visibleAccordionFilters={[]}
        showAccordionToggle={false}
        tools={[
          { key: 'send-sms', label: 'Send SMS', icon: 'sms' }
        ]}
      />

      {/* Data Table */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-left text-sm">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
                <th className="px-5 py-3.5">Name/Address</th>
                <th className="px-5 py-3.5">GST No.</th>
                <th className="px-5 py-3.5">Created On</th>
                <th className="px-5 py-3.5">Last Update</th>
                <th className="w-20 px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-sm text-on-surface-variant font-medium">
                    No entries meet selected criteria.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id} className="transition-colors hover:bg-surface-container-low/40">
                    <td className="px-5 py-3.5">
                      <div className="flex flex-col">
                        <span className="font-bold text-on-surface">{item.name}</span>
                        <span className="text-xs text-on-surface-variant">{item.address}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-on-surface-variant font-mono text-xs">{item.gst}</td>
                    <td className="px-5 py-3.5 text-on-surface-variant font-mono text-xs">{item.createdOn}</td>
                    <td className="px-5 py-3.5 text-on-surface-variant font-mono text-xs">{item.lastUpdate}</td>
                    <td className="px-5 py-3.5 text-right">
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                        items={[
                          { key: 'edit', label: 'Edit', icon: 'edit' },
                          { key: 'archive', label: item.status === 'Active' ? 'Archive' : 'Unarchive', icon: item.status === 'Active' ? 'archive' : 'unarchive' },
                          { key: 'delete', label: 'Delete', icon: 'delete' },
                        ]}
                        onSelect={(key) => {
                          if (key === 'edit') navigate(`/masters/acheads/cust-cash/entry?id=${item.id}`)
                          else if (key === 'archive') handleArchive(item.id)
                          else if (key === 'delete') handleDelete(item.id, item.name)
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {filteredItems.length} entries
          </p>
          <AppPagination
            page={page}
            totalPages={1}
            onPageChange={setPage}
            canPrevious={false}
            canNext={false}
          />
        </div>
      </section>
    </div>
  )
}
